import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rawFetch, rawResponse } from "@/lib/api/client";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";

type Readiness = {
  version_id: string; workspace_slug: string; semver: string; status: string;
  previous_version: string | null;
  sources: Array<{ kind: string; name: string; version: string | number; status: string; license?: string; term_count?: number }>;
  entities: Array<{ name: string; source: string[]; source_iri: string; mapping_status: string }>;
  shacl: { passed: boolean; non_vacuous: boolean; focus_counts: Array<{ shape: string; focus_nodes: number }>; violation_count: number | null };
  tests: { competency_questions: { total: number; passed: number; failed: number; untested: number } };
  rights: { workspace_standard_decisions: Array<{ source_slug: string; mode: string; status: string; source_version: string; selected_modules: string[] }>; note: string };
  diff: { added_entities: string[]; removed_entities: string[]; modified_entities: unknown[]; added_relations: string[]; removed_relations: string[] };
  compatibility: { compatible: boolean; summary: { total: number; high: number; critical: number } };
};
type Gate = { passed: boolean; checks: Record<string, { passed: boolean; required: boolean; error?: string }> };

export function ReleaseReadinessPanel({ versionId }: { versionId: string }) {
  const qc = useQueryClient();
  const { canAdmin } = useWorkspacePermissions();
  const summary = useQuery<Readiness>({
    queryKey: ["ontology-release-readiness", versionId],
    queryFn: () => rawFetch(`/ontology/versions/${versionId}/release-readiness/`),
  });
  const gate = useMutation<Gate, Error>({
    mutationFn: () => rawFetch(`/ontology/versions/${versionId}/release-check/`, {
      method: "POST", body: "{}",
    }),
  });
  const publish = useMutation({
    mutationFn: () => rawFetch(`/ontology/versions/${versionId}/publish/`, {
      method: "POST", body: "{}",
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ontology-release-readiness", versionId] });
      qc.invalidateQueries({ queryKey: ["table", "/ontology/versions/"] });
    },
  });
  const deploy = useMutation({
    mutationFn: async () => {
      if (!summary.data) throw new Error("Load the release record first.");
      const response = await rawResponse("/ontology/orb/export/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspace: summary.data.workspace_slug,
          semver: summary.data.semver, include_shapes: true, sign: true }),
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      try {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `${summary.data.workspace_slug}-${summary.data.semver}.orb`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    },
  });
  const confirmDeployment = useMutation({
    mutationFn: (reference: string) => rawFetch(`/ontology/versions/${versionId}/confirm-deployment/`, {
      method: "POST", body: JSON.stringify({ confirmed: true, reference }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ontology-workspace-journey"] }),
  });
  if (summary.isLoading) return <p role="status" className="text-xs text-muted">Loading release evidence…</p>;
  if (summary.isError) return <p role="alert" className="text-xs text-bad">{summary.error.message}</p>;
  const data = summary.data;
  if (!data) return null;
  const entities = [...data.entities].sort((a, b) => a.source.join().localeCompare(b.source.join()) || a.name.localeCompare(b.name));
  const cqs = data.tests.competency_questions;
  return <div className="space-y-4 text-xs" aria-label="Release readiness">
    <div className="border-b border-border pb-3">
      <h3 className="text-base font-semibold">Release readiness · {data.semver}</h3>
      <p className="mt-1 text-muted">Evidence from the saved draft. Publish reruns the server gate; Deploy downloads a signed ORB for customer-controlled deployment.</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {data.status === "draft" && <button className="btn" disabled={gate.isPending} onClick={() => gate.mutate()}>{gate.isPending ? "Checking…" : "Run release gate"}</button>}
        {data.status === "draft" && canAdmin && <button className="btn-primary" disabled={!gate.data?.passed || publish.isPending}
          onClick={() => { if (window.confirm(`Publish ${data.semver}? The server will rerun all gates.`)) publish.mutate(); }}>Publish</button>}
        <button className="btn-primary" disabled={data.status !== "published" || !canAdmin || deploy.isPending}
          title={data.status !== "published" ? "Publish before deploying" : !canAdmin ? "Workspace admin or owner required" : "Download signed ORB"}
          onClick={() => deploy.mutate()}>{deploy.isPending ? "Preparing…" : "Deploy · download signed ORB"}</button>
        {data.status === "published" && canAdmin && <button className="btn" disabled={confirmDeployment.isPending}
          onClick={() => {
            if (!window.confirm("Record that the customer deployed this version in its own environment? Ontoligent cannot verify that installation remotely.")) return;
            const reference = window.prompt("Optional opaque deployment reference. Do not enter secrets.", "");
            if (reference !== null) confirmDeployment.mutate(reference);
          }}>Record customer deployment</button>}
      </div>
      {gate.data && <p role="status" className={`mt-2 ${gate.data.passed ? "text-good" : "text-bad"}`}>
        Release gate {gate.data.passed ? "passed" : "blocked"}. {Object.entries(gate.data.checks).filter(([, check]) => check.required && !check.passed).map(([name, check]) => `${name}: ${check.error || "incomplete"}`).join("; ")}
      </p>}
      {[gate.error, publish.error, deploy.error, confirmDeployment.error].filter(Boolean).map((error, index) => <p key={index} role="alert" className="mt-2 text-bad">{error?.message}</p>)}
      {publish.isSuccess && <p role="status" className="mt-2 text-good">Published. The signed ORB action is now available.</p>}
      {confirmDeployment.isSuccess && <p role="status" className="mt-2 text-good">Customer-reported deployment recorded; remote installation was not verified.</p>}
    </div>
    <section><h4 className="font-semibold">Pinned sources and rights</h4>
      <p className="mt-1 text-muted">{data.sources.length ? data.sources.map((source) => `${source.kind}: ${source.name} @ ${source.version} (${source.status}${source.license ? `; licence ${source.license}` : ""})`).join(" · ") : "No pinned term sets or packs."}</p>
      <p className="mt-1 text-muted">{data.rights.workspace_standard_decisions.length ? data.rights.workspace_standard_decisions.map((source) => `${source.source_slug}: ${source.mode}, ${source.status}, release ${source.source_version || "unpinned"}${source.selected_modules.length ? `, ${source.selected_modules.join("/")}` : ""}`).join(" · ") : "No standard adoption decisions."}</p>
      <p className="mt-1 text-muted">{data.rights.note}</p>
    </section>
    <section><h4 className="font-semibold">Entities by source · {entities.length}</h4>
      <div className="mt-2 max-h-64 overflow-auto border border-border"><table className="w-full text-left"><thead><tr><th className="p-2">Source</th><th className="p-2">Entity</th><th className="p-2">Mapping</th></tr></thead>
        <tbody>{entities.map((entity) => <tr key={entity.name} className="border-t border-border"><td className="p-2">{entity.source.join(", ")}</td><td className="p-2">{entity.name}</td><td className="p-2">{entity.mapping_status}</td></tr>)}</tbody></table></div>
    </section>
    <div className="grid gap-4 md:grid-cols-2">
      <section><h4 className="font-semibold">SHACL coverage</h4><p className="mt-1">{data.shacl.passed ? "Conforms" : "No passing result"} · {data.shacl.non_vacuous ? "focus nodes present" : "vacuous or untested"} · {data.shacl.violation_count ?? "—"} violations</p>
        {data.shacl.focus_counts.map((item) => <p key={item.shape} className="mt-1 break-all text-muted">{item.shape}: {item.focus_nodes} focus nodes</p>)}</section>
      <section><h4 className="font-semibold">Tests</h4><p className="mt-1">Competency questions: {cqs.passed} passed, {cqs.failed} failed, {cqs.untested} untested of {cqs.total}.</p>
        {(cqs.total === 0 || cqs.untested > 0) && <p className="mt-1 text-warn" role="status">Draft tests have not all run. Open Competency questions to generate and run them before publication.</p>}
      </section>
    </div>
    <section><h4 className="font-semibold">Change from {data.previous_version ?? "first version"}</h4>
      <p className="mt-1 text-muted">{data.diff.added_entities.length} entities added, {data.diff.removed_entities.length} removed, {data.diff.modified_entities.length} modified; {data.diff.added_relations.length} relations added, {data.diff.removed_relations.length} removed. Compatibility findings: {data.compatibility.summary.total} ({data.compatibility.summary.high} high, {data.compatibility.summary.critical} critical).</p>
    </section>
  </div>;
}
